import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  listAllCategories,
  listAllProducts,
  updateProduct,
  deleteProduct,
} from "@/lib/admin.functions";
import { importAktivitetshyraProduct } from "@/lib/import.functions";
import { toast } from "sonner";
import { Loader2, Trash2, RefreshCw, ExternalLink } from "lucide-react";
import { PageHeader, type Cat, type Prod } from "@/components/admin/shared";
import { ProductForm } from "@/components/admin/ProductForm";

export const Route = createFileRoute("/admin/products/$id")({
  component: EditProductPage,
});

function EditProductPage() {
  const { id } = Route.useParams();
  const cats = useServerFn(listAllCategories);
  const list = useServerFn(listAllProducts);
  const update = useServerFn(updateProduct);
  const del = useServerFn(deleteProduct);
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Cat[]>([]);
  const [value, setValue] = useState<Prod | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reimporting, setReimporting] = useState(false);
  const reimport = useServerFn(importAktivitetshyraProduct);

  const doReimport = async () => {
    if (!value?.source_url) return;
    if (!confirm("Hämta produkten igen från källan? Befintliga texter och bilder skrivs över (publiceringsstatus behålls).")) return;
    setReimporting(true);
    try {
      await reimport({ data: { source_url: value.source_url } });
      toast.success("Hämtad igen — laddar om...");
      const ps = (await list({})) as unknown as Prod[];
      const p = ps.find((x) => x.id === id);
      if (p) setValue(p);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setReimporting(false);
    }
  };

  useEffect(() => {
    Promise.all([list({}), cats({})])
      .then(([ps, cs]) => {
        const p = (ps as unknown as Prod[]).find((x) => x.id === id);
        if (!p) throw new Error("Produkten hittades inte");
        setValue(p);
        setCategories(cs as Cat[]);
      })
      .catch((e: any) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  const save = async () => {
    if (!value) return;
    setBusy(true);
    try {
      const payload: any = {
        ...value,
        long_description: value.long_description || null,
        video_url: value.video_url || null,
        dimensions: value.dimensions || null,
        power: value.power || null,
        weight: value.weight || null,
        meta_title: value.meta_title || null,
        meta_description: value.meta_description || null,
      };
      delete payload.id;
      await update({ data: { id: id, patch: payload } });
      toast.success("Sparad");
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!confirm("Ta bort produkten?")) return;
    try {
      await del({ data: { id } });
      toast.success("Borttagen");
      navigate({ to: "/admin/products" });
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  if (loading) return <Loader2 className="h-5 w-5 animate-spin" />;
  if (!value) return <p className="text-sm text-muted-foreground">Hittades inte.</p>;

  return (
    <>
      <PageHeader title={`Redigera: ${value.name}`} backTo="/admin/products">
        {value.source_url && (
          <button
            onClick={doReimport}
            disabled={reimporting}
            className="inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            {reimporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Hämta igen
          </button>
        )}
        <button
          onClick={remove}
          className="inline-flex items-center gap-2 rounded-full bg-destructive text-destructive-foreground px-4 py-2 text-sm font-bold"
        >
          <Trash2 className="h-4 w-4" /> Ta bort
        </button>
      </PageHeader>
      {value.source_url && (
        <div className="mb-4 rounded-lg bg-secondary/50 px-4 py-2 text-xs">
          <span className="text-muted-foreground">Importerad från: </span>
          <a href={value.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold hover:underline">
            {value.source_url} <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      )}
      <ProductForm
        value={value}
        categories={categories}
        busy={busy}
        onChange={setValue}
        onSave={save}
      />
    </>
  );
}

